import { useCallback, useRef, useEffect } from 'react'
import { useWriteQueue } from './useWriteQueue'

export interface PendingWrite<T> {
  data: T
  timeoutId: NodeJS.Timeout
}

export interface DebounceWriteOptions {
  /** 防抖延迟时间（毫秒），默认 800 */
  debounceDelay?: number
  /** 重试延迟时间（毫秒），默认 1500 */
  retryDelay?: number
  /** 写入前回调 */
  onBeforeWrite?: () => void
  /** 写入后回调 */
  onAfterWrite?: () => void
}

/**
 * 防抖写入 Hook
 * 用于减少频繁的数据库写入，避免并发冲突
 */
export function useDebounceWrite<T>(
  writeOperation: (id: string, data: T) => Promise<void>,
  options: DebounceWriteOptions = {}
) {
  const {
    debounceDelay = 800,
    retryDelay = 1500,
    onBeforeWrite,
    onAfterWrite,
  } = options

  const { queueWrite } = useWriteQueue()

  // 待写入数据缓存
  const pendingWrites = useRef<Map<string, PendingWrite<T>>>(new Map())

  /**
   * 防抖写入
   * @param id 唯一标识
   * @param data 要写入的数据
   */
  const debouncedWrite = useCallback(
    (id: string, data: T) => {
      // 取消之前的定时器
      const pending = pendingWrites.current.get(id)
      if (pending) {
        clearTimeout(pending.timeoutId)
      }

      // 设置新的定时器
      const timeoutId = setTimeout(async () => {
        try {
          onBeforeWrite?.()

          await queueWrite(async () => {
            await writeOperation(id, data)
            console.log(`[防抖写入] ${id} 保存成功`)
          })

          pendingWrites.current.delete(id)
          onAfterWrite?.()
        } catch (err) {
          console.error(`[防抖写入失败] ${id}:`, err)
          // 失败后重试
          setTimeout(() => {
            const retryPending = pendingWrites.current.get(id)
            if (retryPending) {
              debouncedWrite(id, retryPending.data)
            }
          }, retryDelay)
        }
      }, debounceDelay)

      pendingWrites.current.set(id, { data, timeoutId })
    },
    [writeOperation, queueWrite, debounceDelay, retryDelay, onBeforeWrite, onAfterWrite]
  )

  /**
   * 强制立即写入
   * @param id 可选，指定 ID 则只写入该条，否则写入所有待处理数据
   */
  const flush = useCallback(
    async (id?: string) => {
      const idsToFlush = id
        ? [id]
        : Array.from(pendingWrites.current.keys())

      if (idsToFlush.length === 0) return

      onBeforeWrite?.()

      const writePromises: Promise<void>[] = []

      for (const writeId of idsToFlush) {
        const pending = pendingWrites.current.get(writeId)
        if (!pending) continue

        clearTimeout(pending.timeoutId)
        pendingWrites.current.delete(writeId)

        writePromises.push(
          queueWrite(async () => {
            await writeOperation(writeId, pending.data)
          }).catch(err => {
            console.error(`[强制写入失败] ${writeId}:`, err)
          })
        )
      }

      await Promise.all(writePromises)
      onAfterWrite?.()
    },
    [writeOperation, queueWrite, onBeforeWrite, onAfterWrite]
  )

  /**
   * 检查是否有待写入的数据
   */
  const hasPending = useCallback((id?: string) => {
    if (id) {
      return pendingWrites.current.has(id)
    }
    return pendingWrites.current.size > 0
  }, [])

  /**
   * 取消待写入
   */
  const cancel = useCallback((id?: string) => {
    if (id) {
      const pending = pendingWrites.current.get(id)
      if (pending) {
        clearTimeout(pending.timeoutId)
        pendingWrites.current.delete(id)
      }
    } else {
      pendingWrites.current.forEach(pending => {
        clearTimeout(pending.timeoutId)
      })
      pendingWrites.current.clear()
    }
  }, [])

  // 监听页面关闭前尝试保存
  useEffect(() => {
    const handleUnload = () => {
      // 同步触发 flush，虽然不能保证异步完成，但能增加保存概率
      flush()
    }
    
    // pagehide 比 beforeunload 在移动端更可靠
    window.addEventListener('beforeunload', handleUnload)
    window.addEventListener('pagehide', handleUnload)
    
    return () => {
      window.removeEventListener('beforeunload', handleUnload)
      window.removeEventListener('pagehide', handleUnload)
    }
  }, [flush])

  // 监听页面可见性变化（用户切换标签时保存）
  useEffect(() => {
    const handleVisibility = () => {
      if (document.visibilityState === 'hidden') {
        flush()
      }
    }
    document.addEventListener('visibilitychange', handleVisibility)
    return () => document.removeEventListener('visibilitychange', handleVisibility)
  }, [flush])

  return {
    debouncedWrite,
    flush,
    hasPending,
    cancel,
  }
}

